import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType } from '../../common/enums.js';
import { sendEmployeeInvitations } from '../../common/fetch.js';
import {
  fieldCantBeEmptyMsg,
  invitationSendingFailedMsg,
  invitationsSentSuccessfullyMsg,
  pleaseCheckEmailMsg,
  t,
} from '../../common/i18n/index.js';
import { MultipleEmails } from '../../common/react-hook-form/fields/index.js';
import { getPrimaryMdmId } from '../../common/utils/accountUtils.js';
import { EMAIL_REGEX } from '../../common/utils/emailUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { FileUpload } from '../FileUpload/FileUpload.js';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import type { CommonError } from '../../common/types/errors.js';

import './InviteEmployeesModal.scss';

export interface InviteEmployeesModalProps {
  onClose: () => void;
  onSubmitted: () => void;
}

const emailsField = 'emails';
const fileUploadField = 'fileUpload';

const parseEmails = (input: string | null) => (input ? input.split(/\r\n|\n|\r/).filter(Boolean) : []);

const validateEmails = (emails: string[], validatedField: string) => {
  if (!emails.length) {
    return validatedField === emailsField
      ? t.VPVR(fieldCantBeEmptyMsg)
      : t.L1QB('The file could not be read or is empty.');
  }
  if (emails.filter(email => EMAIL_REGEX.test(email)).length !== emails.length) {
    return validatedField === emailsField
      ? t.ROMH(pleaseCheckEmailMsg)
      : t.PCNO('The file contains one or more invalid email addresses.');
  }
  if (emails.length > 500) {
    return validatedField === emailsField
      ? t.HYGU('This field can contain up to {} email addresses.', '500')
      : t.VJYS('The file can contain up to 500 email addresses.');
  }
  return undefined;
};

export const InviteEmployeesModal = ({ onClose, onSubmitted }: InviteEmployeesModalProps) => {
  const { authenticatedUser } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | undefined>(undefined);
  const [otherErrors, setOtherErrors] = useState<CommonError[]>([]);
  const [mdmId, setMdmId] = useState<string>(getPrimaryMdmId(authenticatedUser)!);
  const emailsRef = useRef<string | null>('');
  const getFilesRef = useRef<() => File[]>(undefined);
  const methods = useForm<{ emails: string }>({ defaultValues: { emails: '' } });
  const emailsValue = methods.watch('emails');

  useEffect(() => {
    emailsRef.current = emailsValue ?? '';
  }, [emailsValue]);

  const sendInvites = async (emails: string[]) => {
    setLoading(true);
    const response = await sendEmployeeInvitations(
      emails.map(email => ({ email })),
      mdmId
    );
    if (response.ok) {
      addNotification(t.F3KA(invitationsSentSuccessfullyMsg), 'success');
      setLoading(false);
      onSubmitted();
    } else {
      setLoading(false);
      setOtherErrors([
        {
          message: t.A31U(invitationSendingFailedMsg),
          type: CommonErrorType.BAD_REQUEST,
        },
      ]);
    }
  };

  const getValidationErrors = (emailsString: string | null, validatedField: string) => {
    const emails = parseEmails(emailsString);
    const validationError = validateEmails(emails, validatedField);
    if (validatedField === emailsField) {
      methods.setError(emailsField, { message: validationError });
      setFileUploadError(undefined);
    } else if (validatedField === fileUploadField) {
      methods.setError(emailsField, { message: undefined });
      setFileUploadError(validationError);
    }
    if (!validationError) {
      sendInvites(emails);
    }
  };

  const onSubmit = () => {
    const files = getFilesRef.current!();
    if (files.length === 1) {
      const fileReader = new FileReader();
      fileReader.onloadend = () => {
        getValidationErrors(fileReader.result as string | null, fileUploadField);
      };
      fileReader.readAsText(files[0]);
    } else {
      getValidationErrors(emailsRef.current, 'emails');
    }
  };

  return (
    <ConfirmationDialog
      header={t.GVZV('Send invitation to employees')}
      body={
        <>
          <p>
            {t.CI7R('An employee can use the invite link to log into the device store and select a device.')}
            <br />
            {t.FHD7(
              `You will receive confirmation of the employee's device agreement, which you must review and approve.`
            )}
          </p>
          <p>
            {t.M248('You can provide more than one email address at a time or attach e.g. a')}
            <strong> {t.ZB19('csv or text file')}</strong>{' '}
            {t.MRGG('containing email addresses (max. 500). Please note that xlsx file extension is not accepted.')}
          </p>
          <a
            href="https://static.elisa.com/v2/image/2tqybbhjs47b/3EzvYxsNmUAOeJExZlSp7O/employee-onboarding-invitation-tempate.csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.FUAJ('Download here csv-example for email addresses.')}
          </a>
          <FormProvider {...methods}>
            <form className={`of-invite-employees ${dsClass.MARGIN_TOP_3}`}>
              <CompanySelector
                className={dsClass.MARGIN_BOTTOM_3}
                labelText={t.F7YS('Select the company for which you want to invite employees')}
                userAccounts={getUserAccounts(authenticatedUser)}
                initialMdmId={getPrimaryMdmId(authenticatedUser)}
                onInputChange={(_, option) => {
                  setMdmId(option.accountMasterId!);
                }}
              />
              <MultipleEmails />
              <FileUpload
                className="of-invite-employees__file-upload"
                getFilesFn={getFiles => (getFilesRef.current = getFiles)}
                limit={1}
                validationError={fileUploadError}
                allowedFormat={['text/csv', 'text/rtf', 'text/plain', '', 'application/vnd.ms-excel']} // https://christianwood.net/csv-file-upload-validation/
                fileTypeValidationErrorMessage={t.QENF(
                  'Invalid file format. Please save your file either in .csv or .txt format.'
                )}
              />
            </form>
          </FormProvider>
        </>
      }
      isLoading={isLoading}
      onCloseDialog={onClose}
      onConfirm={onSubmit}
      errors={otherErrors}
    />
  );
};
